import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { DeliveryMediaViewers } from '@/components/deliveries';
import { ErrorState, LoadingState, StatusState } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { useDeliveryViewers } from '@/hooks/useDeliveryViewers';

import { gallery as copy } from '../content';
import { useDeliveryGallery } from '../hooks/useDeliveryGallery';
import { DeliveryFilterChips, DeliveryGalleryCount, DeliveryLoadMore } from '../molecules';

import { DeliveryGalleryGrid } from './DeliveryGalleryGrid';

/**
 * The archive: the filters, the wall, and the two viewers it opens.
 *
 * Everything that thinks is in two hooks — `useDeliveryGallery` for what is
 * being shown, `useDeliveryViewers` for what a tap opens — so this file is a
 * list of states and the order they are drawn in. That split is why neither
 * hook needs to know about the other: the viewers are handed the items that
 * are on screen, whatever the filters happened to be.
 *
 * ## The chips stay up in every state
 *
 * Loading, empty and error all render below the chip row rather than instead
 * of it. An empty result is almost always one chip too many, and a page that
 * replaces the filters with "nothing here" has hidden the only control that
 * fixes it.
 *
 * ## No heading, and no call to action, here
 *
 * This band used to open with its own `SectionHeading` and its own "Explore
 * destinations and routes" button, both restating what `PageIntro` already
 * shows above it — the heading verbatim, and the link as the second half of
 * the same primary-action slot `PageIntro` already owns (IA §5: one primary
 * action per screen). `PageIntro` is the page's only heading and only action
 * now (`keepOnPhone` in `DeliveryGalleryPage`, so neither is ever absent, not
 * even on a phone), and this band opens straight into the filters.
 */
export function DeliveryGalleryBand() {
  const gallery = useDeliveryGallery();
  const viewers = useDeliveryViewers({ items: gallery.items, action: copy.viewerCta });

  const hasItems = !gallery.loading && !gallery.error && gallery.items.length > 0;

  return (
    <Section>
      <Stack spacing={6}>
        <DeliveryFilterChips
          facets={gallery.facets}
          destination={gallery.destination}
          media={gallery.media}
          onDestinationChange={gallery.setDestination}
          onMediaChange={gallery.setMedia}
        />

        {/*
          A plain flex Box, not another Stack: MUI's Stack implements its
          `spacing` prop with a high-specificity child-margin reset
          (`.MuiStack-root > :not(style):not(style) { margin: 0 }`), which
          silently overrides a nested StatusState's own `mx: auto` centering
          and left it pinned to this Stack's left edge. `justifyContent:
          center` centers via flex alignment instead of margin, so it is
          immune to that reset.
        */}
        {!hasItems && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {gallery.loading ? (
              <LoadingState label="Loading delivered cars" />
            ) : gallery.error ? (
              <ErrorState error={null} onRetry={gallery.retry} />
            ) : (
              <StatusState
                tone="empty"
                title={copy.empty.title}
                description={copy.empty.text}
                action={{ label: copy.empty.action, onClick: gallery.clearFilters }}
              />
            )}
          </Box>
        )}

        {hasItems && (
          <>
            <DeliveryGalleryCount shown={gallery.items.length} total={gallery.total} />
            <DeliveryGalleryGrid items={gallery.items} onOpen={viewers.openItem} />
            <DeliveryLoadMore
              hasMore={gallery.hasMore}
              loading={gallery.loadingMore}
              onLoadMore={gallery.loadMore}
            />
          </>
        )}
      </Stack>

      <DeliveryMediaViewers viewers={viewers} />
    </Section>
  );
}
